import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { Network } from '@root/entities/network'
import { TradeSettings } from '@root/entities/trade-settings'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { AntiMevShield } from '@root/features/trade/trade-settings/ui/desktop'
import { useTranslation } from '@root/shared/localization'
import {
  PetrolIcon,
  RunnerIcon,
  Separator,
  SharingHandIcon,
} from '@root/shared/ui-kit'

import { openInstantTradeDialogAction } from '../../atoms'

import type { TradeType } from '@root/entities/trade-type'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  swapType: TradeType.SwapDirection
  withMevProtection?: boolean
  iconsSize?: number
  children?: React.ReactNode
} & ClassNameProps

export const InstantTradeSwapGas: React.FC<Props> = ({
  className,
  children,
  withMevProtection = true,
  iconsSize = 16,
}) => {
  const { t } = useTranslation()
  const [tradeSettings] = useAtom(tradeSettingsAtom)
  const setTab = useAction(openInstantTradeDialogAction)

  const handleSettingsOpen = () => {
    setTab('settings')
  }

  const priorityFee = useMemo(() => {
    if (tradeSettings.network === Network.Solana) {
      return tradeSettings.priorityFee
    }

    if (tradeSettings.network === Network.Ethereum) {
      return tradeSettings.gasPrice ?? tradeSettings.maxGasPrice
    }

    return tradeSettings.gasPrice
  }, [tradeSettings])

  const bribe = useMemo(() => {
    if (tradeSettings.network === Network.Solana && tradeSettings.jitoEnabled) {
      return tradeSettings.jitoTip
    }

    if (tradeSettings.network === Network.Ethereum && !tradeSettings.gasPrice) {
      return tradeSettings.maxPriorityFeePerGas
    }

    return undefined
  }, [tradeSettings])

  const antiMevEnabled = TradeSettings.isAntiMevEnabled(tradeSettings)

  return (
    <Root className={className}>
      <Tooltip content={t('trading.order.gas-priority-fee-tooltip')}>
        <ButtonAlt type="button" onClick={handleSettingsOpen}>
          <PetrolIcon size={iconsSize} />
          <Numeric value={priorityFee ?? 0} preset="currency-amount-short" />
        </ButtonAlt>
      </Tooltip>
      <Separator_ vertical />
      <Tooltip content={t('trading.order.slippage-percentage-tooltip')}>
        <ButtonAlt type="button" onClick={handleSettingsOpen}>
          <RunnerIcon size={iconsSize} />
          <span>
            <Numeric value={tradeSettings.slippage} preset="percents" />%
          </span>
        </ButtonAlt>
      </Tooltip>

      {bribe ? (
        <>
          <Separator_ vertical />
          <Tooltip content={t('trading.order.bribe-tooltip')}>
            <ButtonAlt type="button" onClick={handleSettingsOpen}>
              <SharingHandIcon size={iconsSize} />
              <Numeric value={bribe} preset="amount-max-5-digits" />
            </ButtonAlt>
          </Tooltip>
        </>
      ) : undefined}
      {withMevProtection ? (
        <>
          <Separator_ vertical />
          <Mev onClick={handleSettingsOpen}>
            <AntiMevShield active={antiMevEnabled ?? false} size={22} />
          </Mev>
        </>
      ) : undefined}
      <Right>{children}</Right>
    </Root>
  )
}

const Root = styled.div`
  --instant-trade-swap-gas-gap: 6px;
  --instant-trade-swap-gas-font-size: 11px;
  --instant-trade-swap-gas-separator-height: 20px;

  display: flex;
  grid-auto-flow: column;
  grid-auto-columns: auto;

  justify-content: start;
  align-items: center;

  gap: var(--instant-trade-swap-gas-gap);
  margin-top: 2px;
`

const Right = styled.div`
  margin-left: auto;
`

const ButtonAlt = styled.button`
  all: unset;
  color: var(--text-11);
  cursor: pointer;

  font-size: var(--instant-trade-swap-gas-font-size);
  border-radius: 6px;
  font-weight: 500;

  display: grid;
  grid-auto-flow: column;
  gap: 2px;
  align-items: center;

  &:hover {
    background: transparent;
  }
`

const Mev = styled.div`
  font-size: 11px;
  font-weight: 500;

  cursor: pointer;

  display: grid;
  align-items: center;
  justify-content: end;
  grid-template-columns: 1fr auto auto;
  justify-items: end;
`

const Separator_ = styled(Separator)`
  background: var(--background-3);
  height: 14px;
`
